import React from 'react';
import { PlayIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, SparklesIcon, MicIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
const PARTICIPATION_LEVELS = ['high', 'medium', 'medium', 'low', 'very low'];
function buildParticipants(crewRoles: {
  name: string;
  role: string;
}[]) {
  return crewRoles.map((cr, i) => ({
    name: cr.name,
    role: cr.role,
    level: PARTICIPATION_LEVELS[i % PARTICIPATION_LEVELS.length]
  }));
}
const levelColor: Record<string, string> = {
  high: 'border-green-200 text-green-600',
  medium: 'border-amber-200 text-amber-600',
  low: 'border-red-200 text-red-500',
  'very low': 'border-red-200 text-red-500'
};
const levelDot: Record<string, string> = {
  high: 'bg-green-500',
  medium: 'bg-amber-500',
  low: 'bg-red-500',
  'very low': 'bg-red-500'
};
export function Screen13_RecordingComplete({
  data,
  onNext,
  onBack
}: ScreenProps) {
  const PARTICIPANTS = buildParticipants(data.crewRoles);
  return <div className="flex flex-col h-full" data-id="element-985">
      <div className="mb-4" data-id="element-986">
        <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-987">
          Meeting Recording Complete
        </h2>
        <p className="text-gray-500 text-sm" data-id="element-988">
          AI finished analyzing the PTP meeting.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-989">
        {/* Meeting Info */}
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-990">
          <div className="grid grid-cols-2 gap-3" data-id="element-991">
            <div data-id="element-992">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold" data-id="element-993">
                Duration
              </p>
              <p className="text-gray-900 font-medium text-sm" data-id="element-994">04:36</p>
            </div>
            <div data-id="element-995">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold" data-id="element-996">
                Date
              </p>
              <p className="text-gray-900 font-medium text-sm" data-id="element-997">June 12, 2026</p>
            </div>
          </div>
        </div>

        {/* Participants — minimal tags with participation level */}
        <div data-id="element-998">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2" data-id="element-999">
            Participants
          </p>
          <div className="flex flex-wrap gap-1.5" data-id="element-1000">
            {PARTICIPANTS.map((p, i) => <span key={i} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium bg-white border ${levelColor[p.level]}`} data-id="element-1001">
                <span className={`w-1.5 h-1.5 rounded-full ${levelDot[p.level]}`} data-id="element-1002" />
                <span className="text-gray-700 font-semibold" data-id="element-1003">
                  {p.name.split(' ')[0]}
                </span>
                <span className="text-gray-400" data-id="element-1004">·</span>
                <span data-id="element-1005">{p.role}</span>
              </span>)}
          </div>
        </div>

        {/* Topics */}
        <div className="space-y-3" data-id="element-1006">
          <div className="bg-green-50 border border-green-200 rounded-xl p-3" data-id="element-1007">
            <div className="flex items-center gap-2 mb-2" data-id="element-1008">
              <CheckCircleIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1009" />
              <h4 className="text-[10px] font-bold text-green-600 uppercase tracking-wider" data-id="element-1010">
                Covered
              </h4>
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-1011">
              {['Job steps', ...(data.stepHazardData || []).slice(0, 4).map((s: any) => s.description), 'Safety controls'].map((topic, i) => <span key={i} className="bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded text-[11px]" data-id="element-1012">
                  {topic}
                </span>)}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-id="element-1013">
            <div className="flex items-center gap-2 mb-2" data-id="element-1014">
              <AlertTriangleIcon className="w-3.5 h-3.5 text-amber-500" data-id="element-1015" />
              <h4 className="text-[10px] font-bold text-amber-600 uppercase tracking-wider" data-id="element-1016">
                Partially Covered
              </h4>
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-1017">
              {['Hazard controls', 'Work area conditions'].map((topic, i) => <span key={i} className="bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded text-[11px]" data-id="element-1018">
                  {topic}
                </span>)}
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-id="element-1019">
            <div className="flex items-center gap-2 mb-2" data-id="element-1020">
              <XCircleIcon className="w-3.5 h-3.5 text-red-500" data-id="element-1021" />
              <h4 className="text-[10px] font-bold text-red-500 uppercase tracking-wider" data-id="element-1022">
                Missing
              </h4>
            </div>
            <ul className="space-y-1.5" data-id="element-1023">
              {['Changing environmental conditions', 'Emergency stop procedure', 'Response to unexpected hazards', 'Full PPE confirmation from all crew members'].map((topic, i) => <li key={i} className="flex items-start gap-2 text-red-700 text-xs" data-id="element-1024">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" data-id="element-1025" />
                  {topic}
                </li>)}
            </ul>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-2 bg-gray-50" data-id="element-1026">
        <PrimaryButton onClick={onNext} icon={<SparklesIcon className="w-5 h-5" data-id="element-1028" />} data-id="element-1027">
          View AI Review
        </PrimaryButton>
        <div className="flex gap-2" data-id="element-1029">
          <SecondaryButton onClick={onBack} icon={<MicIcon className="w-4 h-4" data-id="element-1031" />} data-id="element-1030">
            Continue Meeting
          </SecondaryButton>
          <SecondaryButton onClick={() => {}} icon={<PlayIcon className="w-4 h-4 fill-current" data-id="element-1033" />} data-id="element-1032">
            Play Recording
          </SecondaryButton>
        </div>
      </div>
    </div>;
}